import type { CrmPreviewHandle, PreviewOverlay } from './types';

function areEqual(
  a: PreviewOverlay | undefined,
  b: PreviewOverlay | undefined
): boolean {
  if (a === b) {
    return true;
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export interface PreviewStore {
  handle: CrmPreviewHandle;
  setOverlays(next: ReadonlyMap<string, PreviewOverlay>): void;
  clear(): void;
}

export function createPreviewStore(): PreviewStore {
  let overlays = new Map<string, PreviewOverlay>();
  const listeners = new Map<string, Set<() => void>>();

  function notify(id: string): void {
    const subs = listeners.get(id);
    if (!subs) {
      return;
    }
    for (const cb of subs) {
      cb();
    }
  }

  const handle: CrmPreviewHandle = {
    getOverlay(entityId: string): PreviewOverlay | undefined {
      return overlays.get(entityId);
    },
    hasPreview(entityId: string): boolean {
      return overlays.has(entityId);
    },
    subscribeToOverlay(entityId: string, cb: () => void): () => void {
      let subs = listeners.get(entityId);
      if (!subs) {
        subs = new Set();
        listeners.set(entityId, subs);
      }
      subs.add(cb);
      return () => {
        const current = listeners.get(entityId);
        if (!current) {
          return;
        }
        current.delete(cb);
        if (current.size === 0) {
          listeners.delete(entityId);
        }
      };
    },
  };

  function setOverlays(next: ReadonlyMap<string, PreviewOverlay>): void {
    const ids = new Set<string>([...overlays.keys(), ...next.keys()]);
    const changed: string[] = [];
    const nextOverlays = new Map<string, PreviewOverlay>();

    for (const id of ids) {
      const current = overlays.get(id);
      const incoming = next.get(id);
      if (areEqual(current, incoming)) {
        if (current !== undefined) {
          nextOverlays.set(id, current);
        }
        continue;
      }
      if (incoming !== undefined) {
        nextOverlays.set(id, incoming);
      }
      changed.push(id);
    }

    overlays = nextOverlays;
    for (const id of changed) {
      notify(id);
    }
  }

  function clear(): void {
    const ids = [...overlays.keys()];
    overlays = new Map();
    for (const id of ids) {
      notify(id);
    }
  }

  return { handle, setOverlays, clear };
}
